import {
  clearChoreographySubjectToken, getChoreographyRefreshToken, getChoreographySubjectToken,
  setChoreographyRefreshToken, setChoreographySubjectToken,
} from './authStorage';

const CLIENT_ID = 'choreo-local';
const SCOPES = 'openid profile email offline_access';
const TRANSACTION_PREFIX = 'studioaeye_choreography_oauth_transaction:';
const TRANSACTION_TTL_MS = 5 * 60 * 1000;
let callbackExchangePromise = null;
let refreshPromise = null;

export const CHOREOGRAPHY_CALLBACK_PATH = '/app/choreo/auth/studioaeye/callback';
export const CHOREOGRAPHY_LOCAL_CALLBACK_PATH = '/auth/studioaeye/callback';
export const CHOREOGRAPHY_RETURN_PATH = '/app/choreography';

export function isLocalChoreographyOrigin(location = window.location) {
  return location.protocol === 'http:'
    && location.hostname === 'localhost'
    && location.port === '3000';
}

export function choreographyCallbackPath() {
  return isLocalChoreographyOrigin() ? CHOREOGRAPHY_LOCAL_CALLBACK_PATH : CHOREOGRAPHY_CALLBACK_PATH;
}

export function choreographyRedirectUri() {
  return `${window.location.origin}${choreographyCallbackPath()}`;
}

export function safeChoreographyReturnPath(value) {
  const candidate = String(value || '');
  if (candidate === CHOREOGRAPHY_RETURN_PATH) return candidate;
  if (/^\/app\/choreography\/review\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(candidate)) return candidate;
  return CHOREOGRAPHY_RETURN_PATH;
}

function base64Url(bytes) {
  let binary = ''; bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomValue(byteLength) {
  if (!window.crypto?.getRandomValues) throw new Error('Secure browser randomness is required for Choreography sign-in.');
  const bytes = new Uint8Array(byteLength); window.crypto.getRandomValues(bytes); return base64Url(bytes);
}

async function pkceChallenge(verifier) {
  if (!window.crypto?.subtle?.digest) throw new Error('Secure PKCE is unavailable in this browser.');
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

function clearExpiredTransactions(now = Date.now()) {
  for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = window.sessionStorage.key(index); if (!key?.startsWith(TRANSACTION_PREFIX)) continue;
    try {
      const transaction = JSON.parse(window.sessionStorage.getItem(key) || 'null');
      if (!transaction?.createdAt || now - Number(transaction.createdAt) > TRANSACTION_TTL_MS) window.sessionStorage.removeItem(key);
    } catch { window.sessionStorage.removeItem(key); }
  }
}

function tokenError(message, code = 'CHOREO_SUBJECT_TOKEN_UNAVAILABLE') {
  const error = new Error(message); error.code = code; return error;
}

// GitLab production report 2026-08-23 (org 44): "I have added a nested folder
// in video lib and I can't add a second." Real production evidence (nginx
// access log + choreography_media_folders read for org 44, both same day)
// showed the choreo backend NEVER rejected a folder create and had ZERO
// requests of ANY kind for ~33 minutes despite the owner presumably trying --
// consistent with something client-side blocking the request from ever being
// SENT, not the server refusing it. Every choreo API call goes through the
// `api` axios instance's request interceptor, which `await`s
// `ensureChoreographySubjectToken()` (below) BEFORE the real request is
// issued -- and that token exchange used a bare `window.fetch` with NO
// timeout, unlike every axios-based call in api.js (all of which carry an
// explicit `timeout`, api.js:13/24). A slow or momentarily unresponsive
// `/oauth/token` round trip (this endpoint is proxied to Core, MCRO001-D019
// §4.0.0) would hang this fetch indefinitely: the interceptor's `await`
// never settles, so `choreographyApi.createFolder(...)` -- or ANY choreo
// call -- never actually leaves the browser. That produces exactly what was
// observed: a stuck "Creating…" button, zero server-side log entries for as
// long as the hang lasts, and eventual recovery once the underlying
// connection is torn down (by the OS/browser network stack) or the page is
// reloaded -- both of which reset the in-flight promise. This bounds that
// wait the same way api.js already bounds every other request, so a stalled
// sign-in fails fast with a real, actionable error instead of hanging the
// entire Choreography UI silently.
const TOKEN_REQUEST_TIMEOUT_MS = 10000;

async function tokenRequest(body) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TOKEN_REQUEST_TIMEOUT_MS);
  let response;
  try {
    response = await window.fetch('/oauth/token', {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(body).toString(),
      signal: controller.signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') {
      throw tokenError('Choreography sign-in is taking too long. Please try again.', 'CHOREO_OIDC_TIMEOUT');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw tokenError(payload.message || payload.error_description || 'Choreography sign-in failed.', payload.error || 'CHOREO_OIDC_EXCHANGE_FAILED');
  if (payload.token_type !== 'Bearer' || !String(payload.access_token || '').startsWith('sae_at_')
    || !Number.isFinite(Number(payload.expires_in)) || Number(payload.expires_in) <= 30) {
    throw tokenError('StudioAEye Connect returned an invalid Choreography token.', 'CHOREO_OIDC_INVALID_TOKEN');
  }
  if (payload.refresh_token && !String(payload.refresh_token).startsWith('sae_rt_')) {
    throw tokenError('StudioAEye Connect returned an invalid refresh token.', 'CHOREO_OIDC_INVALID_TOKEN');
  }
  if (!String(payload.refresh_token || '').startsWith('sae_rt_')) {
    throw tokenError('StudioAEye Connect did not return the required rotating refresh token.', 'CHOREO_OIDC_INVALID_TOKEN');
  }
  setChoreographySubjectToken(payload.access_token, Date.now() + Number(payload.expires_in) * 1000);
  if (payload.refresh_token) setChoreographyRefreshToken(payload.refresh_token);
  return payload.access_token;
}

export async function prepareChoreographyAuthorization(returnPath = CHOREOGRAPHY_RETURN_PATH) {
  clearChoreographySubjectToken(); clearExpiredTransactions();
  const verifier = randomValue(64); const state = randomValue(32); const nonce = randomValue(32);
  window.sessionStorage.setItem(`${TRANSACTION_PREFIX}${state}`, JSON.stringify({
    verifier, nonce, returnPath: safeChoreographyReturnPath(returnPath), redirectUri: choreographyRedirectUri(), createdAt: Date.now(),
  }));
  const params = new URLSearchParams({
    response_type: 'code', client_id: CLIENT_ID, redirect_uri: choreographyRedirectUri(), scope: SCOPES,
    state, nonce, code_challenge: await pkceChallenge(verifier), code_challenge_method: 'S256',
  });
  return `/oauth/authorize?${params.toString()}`;
}

export async function beginChoreographyAuthorization(returnPath = CHOREOGRAPHY_RETURN_PATH) {
  window.location.assign(await prepareChoreographyAuthorization(returnPath));
}

async function exchangeChoreographyAuthorization(search) {
  const params = new URLSearchParams(search); const state = params.get('state') || '';
  const transactionKey = `${TRANSACTION_PREFIX}${state}`; let transaction;
  try { transaction = JSON.parse(window.sessionStorage.getItem(transactionKey) || 'null'); } catch { transaction = null; }
  const age = Date.now() - Number(transaction?.createdAt || 0);
  if (!state || !transaction?.verifier || !transaction.redirectUri || age < 0 || age > TRANSACTION_TTL_MS) {
    throw tokenError('The Choreography sign-in response could not be verified.', 'CHOREO_OIDC_STATE_INVALID');
  }
  window.sessionStorage.removeItem(transactionKey);
  if (params.get('error')) throw tokenError(params.get('error_description') || 'Choreography sign-in was denied.', params.get('error'));
  const code = params.get('code') || '';
  if (!code) throw tokenError('The Choreography sign-in response did not include an authorization code.', 'CHOREO_OIDC_CODE_MISSING');
  await tokenRequest({ grant_type: 'authorization_code', code, client_id: CLIENT_ID,
    redirect_uri: transaction.redirectUri, code_verifier: transaction.verifier });
  return safeChoreographyReturnPath(transaction.returnPath);
}

export function completeChoreographyAuthorization(search) {
  if (!callbackExchangePromise) {
    const exchange = exchangeChoreographyAuthorization(search); callbackExchangePromise = exchange;
    const clear = () => { if (callbackExchangePromise === exchange) callbackExchangePromise = null; };
    exchange.then(clear, clear);
  }
  return callbackExchangePromise;
}

export async function ensureChoreographySubjectToken() {
  const current = getChoreographySubjectToken(); if (current) return current;
  const refreshToken = getChoreographyRefreshToken();
  if (!refreshToken) throw tokenError('Choreography authorization is required.');
  if (!refreshPromise) {
    refreshPromise = tokenRequest({ grant_type: 'refresh_token', refresh_token: refreshToken })
      .catch((error) => { clearChoreographySubjectToken(); throw error; })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

export async function revokeChoreographyAuthorization() {
  const refreshToken = getChoreographyRefreshToken(); const accessToken = getChoreographySubjectToken();
  clearChoreographySubjectToken();
  const token = refreshToken || accessToken; if (!token) return;
  await window.fetch('/oauth/revoke', { method: 'POST', credentials: 'same-origin',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: new URLSearchParams({ token }).toString() }).catch(() => undefined);
}

// Compatibility aliases for owner features that already consume the Choreo
// token accessor while storage and refresh are now centralised in authStorage.
export const getChoreographyAccessToken = () => getChoreographySubjectToken() || '';
export const clearChoreographyAccessToken = clearChoreographySubjectToken;
